import { useState, useEffect } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { BookOpen, Plus, Clock, CheckCircle, XCircle, ShoppingBag, Camera, X, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const etatLabels = {
  neuf: "Neuf",
  bon_etat: "Bon état",
  usage: "Usagé"
};

const statutConfig = {
  en_attente: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  accepte: { label: "Accepté - À déposer", color: "bg-green-100 text-green-800", icon: CheckCircle },
  en_vente: { label: "En vente", color: "bg-blue-100 text-blue-800", icon: ShoppingBag },
  vendu: { label: "Vendu", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  refuse: { label: "Refusé", color: "bg-red-100 text-red-800", icon: XCircle },
  expire: { label: "Expiré", color: "bg-gray-100 text-gray-600", icon: Clock },
};

const ClientPortalBooks = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [formPhotos, setFormPhotos] = useState([]);
  const [form, setForm] = useState({
    titre: "",
    auteur: "",
    matiere_classe: "",
    etat: "bon_etat",
    prix_souhaite: "",
    description: ""
  });

  const fetchProposals = async () => {
    try {
      const res = await API.get("/portal/book-proposals");
      setProposals(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProposals(); }, []);

  const uploadPhotos = async (proposalId, files) => {
    setUploadingFor(proposalId);
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }
      await API.post(`/portal/book-proposals/${proposalId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo(s) ajoutée(s)");
      fetchProposals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur upload");
    } finally {
      setUploadingFor(null);
    }
  };

  const deletePhoto = async (proposalId, photoUrl) => {
    try {
      await API.delete(`/portal/book-proposals/${proposalId}/photos`, { params: { photo_url: photoUrl } });
      toast.success("Photo supprimée");
      fetchProposals();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }
    setSubmitting(true);
    try {
      const data = { ...form };
      if (data.prix_souhaite) {
        data.prix_souhaite = parseFloat(data.prix_souhaite);
      } else {
        delete data.prix_souhaite;
      }
      if (!data.auteur) delete data.auteur;
      if (!data.matiere_classe) delete data.matiere_classe;
      if (!data.description) delete data.description;

      const res = await API.post("/portal/book-proposals", data);
      const newId = res.data?.id;

      // Upload photos if any
      if (newId && formPhotos.length > 0) {
        const formData = new FormData();
        for (const file of formPhotos) {
          formData.append("files", file);
        }
        await API.post(`/portal/book-proposals/${newId}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      toast.success("Votre proposition a été envoyée !");
      setShowForm(false);
      setForm({ titre: "", auteur: "", matiere_classe: "", etat: "bon_etat", prix_souhaite: "", description: "" });
      setFormPhotos([]);
      fetchProposals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="client-books-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Livres scolaires
          </h1>
          <p className="text-muted-foreground text-sm">Proposez vos livres à la revente</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} data-testid="propose-book-btn">
          <Plus className="h-4 w-4 mr-2" />
          Proposer un livre
        </Button>
      </div>

      {/* Formulaire de proposition */}
      {showForm && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Proposer un livre</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Titre du livre *</Label>
                  <Input
                    value={form.titre}
                    onChange={(e) => setForm({ ...form, titre: e.target.value })}
                    placeholder="Ex: Maths 3ème - Transmath"
                    required
                    data-testid="book-title-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Auteur / Éditeur</Label>
                  <Input
                    value={form.auteur}
                    onChange={(e) => setForm({ ...form, auteur: e.target.value })}
                    placeholder="Ex: Nathan, Hachette..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Matière / Classe</Label>
                  <Input
                    value={form.matiere_classe}
                    onChange={(e) => setForm({ ...form, matiere_classe: e.target.value })}
                    placeholder="Ex: Français 4ème, SVT Terminale..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>État du livre *</Label>
                  <select
                    value={form.etat}
                    onChange={(e) => setForm({ ...form, etat: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="book-etat-select"
                  >
                    <option value="neuf">Neuf</option>
                    <option value="bon_etat">Bon état</option>
                    <option value="usage">Usagé</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Prix souhaité (EUR) *</Label>
                  <Input
                    type="number"
                    step="0.50"
                    min="0"
                    value={form.prix_souhaite}
                    onChange={(e) => setForm({ ...form, prix_souhaite: e.target.value })}
                    placeholder="Ex: 5.00"
                    required
                    data-testid="book-price-input"
                  />
                  <p className="text-xs text-muted-foreground">Indiquez 0 si vous souhaitez offrir le livre.</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Description / Commentaires</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Annotations, pages manquantes, couverture abîmée..."
                  rows={2}
                />
              </div>
              {/* Photos upload */}
              <div className="space-y-1.5">
                <Label>Photos du livre (max 3)</Label>
                <div className="flex flex-wrap gap-2 items-center">
                  {formPhotos.map((file, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Photo ${idx + 1}`}
                        className="h-20 w-20 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormPhotos(formPhotos.filter((_, i) => i !== idx))}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {formPhotos.length < 3 && (
                    <label className="h-20 w-20 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                      <Camera className="h-5 w-5 text-muted-foreground/50" />
                      <span className="text-[10px] text-muted-foreground mt-0.5">Ajouter</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        multiple
                        onChange={(e) => {
                          if (e.target.files?.length) {
                            const newFiles = Array.from(e.target.files).slice(0, 3 - formPhotos.length);
                            setFormPhotos([...formPhotos, ...newFiles]);
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={submitting} data-testid="submit-book-btn">
                  {submitting ? "Envoi..." : "Envoyer la proposition"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Liste des propositions */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : proposals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="font-medium">Aucune proposition</p>
            <p className="text-sm text-muted-foreground mb-4">Proposez vos livres scolaires pour la revente en boutique</p>
            <Button variant="outline" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Proposer un livre
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {proposals.map((p) => {
            const config = statutConfig[p.statut] || statutConfig.en_attente;
            const StatusIcon = config.icon;
            return (
              <Card key={p.id} data-testid={`proposal-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">{p.titre}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                        {p.auteur && <span>{p.auteur}</span>}
                        {p.matiere_classe && <span>{p.matiere_classe}</span>}
                        <span>{etatLabels[p.etat] || p.etat}</span>
                      </div>
                      {p.description && <p className="text-sm text-muted-foreground mt-1">{p.description}</p>}
                      <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        {p.prix_souhaite > 0 && (
                          <span className="text-muted-foreground">Prix souhaité : <strong>{p.prix_souhaite.toFixed(2)} EUR</strong></span>
                        )}
                        {p.prix_vente > 0 && (
                          <span className="text-primary font-medium">Prix de vente : {p.prix_vente.toFixed(2)} EUR</span>
                        )}
                        {p.prix_rachat > 0 && (
                          <span className="text-green-600 font-medium">Prix de rachat : {p.prix_rachat.toFixed(2)} EUR</span>
                        )}
                      </div>
                      {p.statut === "refuse" && p.motif_refus && (
                        <p className="text-sm text-red-600 mt-1">Motif : {p.motif_refus}</p>
                      )}
                      {p.statut === "accepte" && (
                        <div className="mt-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm">
                          <p className="font-medium text-green-800">Votre livre a été accepté !</p>
                          <p className="text-green-700 mt-1">
                            Rachat : <strong>{(p.prix_rachat || 0).toFixed(2)} EUR</strong> — Déposez votre livre à la boutique de Bergevin (Super U, Pointe-à-Pitre) pour récupérer votre paiement.
                          </p>
                          {p.date_limite_depot && (
                            <p className="text-green-600 mt-1 font-medium">
                              Dépôt avant le {new Date(p.date_limite_depot).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                            </p>
                          )}
                        </div>
                      )}
                      {p.statut === "en_vente" && (
                        <p className="text-sm text-blue-600 mt-2 font-medium">Livre réceptionné et en vente dans la boutique</p>
                      )}
                    </div>
                    <Badge className={config.color + " shrink-0 gap-1"}>
                      <StatusIcon className="h-3 w-3" />
                      {config.label}
                    </Badge>
                  </div>

                  {/* Photos */}
                  {(p.photos?.length > 0 || (p.statut === "en_attente" || p.statut === "accepte")) && (
                    <div className="mt-3">
                      <div className="flex flex-wrap gap-2 items-center">
                        {(p.photos || []).map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img
                              src={`${BACKEND}${photo}`}
                              alt={`Photo ${idx + 1}`}
                              className="h-20 w-20 object-cover rounded-lg border"
                            />
                            {(p.statut === "en_attente" || p.statut === "accepte") && (
                              <button
                                onClick={() => deletePhoto(p.id, photo)}
                                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                        {(p.photos || []).length < 3 && (p.statut === "en_attente" || p.statut === "accepte") && (
                          <label className="h-20 w-20 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                            {uploadingFor === p.id ? (
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : (
                              <>
                                <Camera className="h-5 w-5 text-muted-foreground/50" />
                                <span className="text-[10px] text-muted-foreground mt-0.5">Photo</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              multiple
                              onChange={(e) => {
                                if (e.target.files?.length) uploadPhotos(p.id, Array.from(e.target.files));
                              }}
                              disabled={uploadingFor === p.id}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Proposé le {new Date(p.created_at).toLocaleDateString("fr-FR")}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientPortalBooks;
